import {
  Avatar,
  Card,
  CardContent,
  CardHeader,
  CardProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetChargingSessionStatusText } from 'src/shared/charging-status-card/use-get-charging-session-status-text';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import { TransactionStatusModel } from '@delmonicos/shared-sdk/libs/emsp/src/libs/shared/src/transaction-service/transaction.model';
import { SkeletonTable } from '../skeleton/skeleton-table';

type ChargingStatusCardProps = CardProps & {
  chargingSessionStatusList?: TransactionStatusModel[];
  isPending?: boolean;
};

export function ChargingStatusCard(
  props: ChargingStatusCardProps,
): React.JSX.Element {
  const {
    chargingSessionStatusList = [],
    isPending = false,
    sx,
    ...cardProps
  } = props;

  const { t } = useTranslation();

  const getChargingSessionStatus = useGetChargingSessionStatusText();

  return (
    <Card sx={{ overflow: 'unset', ...sx }} {...cardProps}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'primary.light' }}>
            <LowPriorityIcon color="primary" />
          </Avatar>
        }
        title={t('charging-session.status.title')}
      />

      <TableContainer component={CardContent} sx={{ maxWidth: 800 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('charging-session.status.date')}</TableCell>
              <TableCell>{t('charging-session.status.order')}</TableCell>
              <TableCell>{t('charging-session.status.session')}</TableCell>
              <TableCell>
                {t('charging-session.status.ocpiSessionId')}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isPending && <SkeletonTable rows={4} cols={4} />}

            {!isPending && chargingSessionStatusList.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  {t('charging-session.status.fallback')}
                </TableCell>
              </TableRow>
            )}

            {!isPending &&
              chargingSessionStatusList.map((row) => (
                <TableRow key={new Date(row.updatedAt).getTime()}>
                  <Tooltip
                    title={t('charging-session.status.updated-at-full', {
                      value: row.updatedAt,
                    })}>
                    <TableCell>
                      {t('charging-session.status.updated-at', {
                        value: row.updatedAt,
                      })}
                    </TableCell>
                  </Tooltip>

                  <TableCell
                    colSpan={2}
                    sx={{
                      paddingLeft: 2,
                    }}>
                    {getChargingSessionStatus(row.type)}
                  </TableCell>
                  <TableCell>{row.ocpiSessionId}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
